const STORAGE_KEY = 'suppressRecurringModal';
const SOLD_OUT_STORAGE_KEY = 'suppressSoldOutRecurringModal';
const RECURRING_LIMIT_DISMISSED_KEY = 'recurringLimitWarningDismissed';
const RECURRING_SESSION_COUNT_KEY = 'recurringSessionAddCount';

export const RECURRING_LIMIT_THRESHOLD = 4;

/**
 * Checks if the user has opted to suppress the add-to-recurring confirmation modal.
 *
 * @returns {boolean} True if the modal should be suppressed, false otherwise
 */
export const shouldSuppressRecurringModal = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Persists the user's preference to suppress the add-to-recurring confirmation modal.
 *
 * @param {boolean} suppress - Whether to suppress the modal
 */
export const setSuppressRecurringModal = (suppress) => {
    try {
        if (suppress) {
            localStorage.setItem(STORAGE_KEY, 'true');
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // localStorage may not be available in some contexts
    }
};

/**
 * Checks if the user has opted to suppress the sold-out recurring confirmation modal.
 *
 * @returns {boolean} True if the modal should be suppressed, false otherwise
 */
export const shouldSuppressSoldOutRecurringModal = () => {
    try {
        return localStorage.getItem(SOLD_OUT_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Persists the user's preference to suppress the sold-out recurring confirmation modal.
 *
 * @param {boolean} suppress - Whether to suppress the modal
 */
export const setSuppressSoldOutRecurringModal = (suppress) => {
    try {
        if (suppress) {
            localStorage.setItem(SOLD_OUT_STORAGE_KEY, 'true');
        } else {
            localStorage.removeItem(SOLD_OUT_STORAGE_KEY);
        }
    } catch {
        // localStorage may not be available in some contexts
    }
};

/**
 * Gets the current recurring additions count for this session.
 *
 * @returns {number} The number of recurring items added this session
 */
export const getRecurringSessionCount = () => {
    try {
        const count = sessionStorage.getItem(RECURRING_SESSION_COUNT_KEY);
        return count ? parseInt(count, 10) : 0;
    } catch {
        return 0;
    }
};

/**
 * Increments the recurring additions count for this session.
 *
 * @returns {number} The new count after incrementing
 */
export const incrementRecurringSessionCount = () => {
    try {
        const currentCount = getRecurringSessionCount();
        const newCount = currentCount + 1;
        sessionStorage.setItem(RECURRING_SESSION_COUNT_KEY, String(newCount));
        return newCount;
    } catch {
        return 0;
    }
};

/**
 * Checks if the recurring limit warning has been permanently dismissed by the user.
 *
 * @returns {boolean} True if the warning should never be shown again
 */
export const isRecurringLimitWarningDismissed = () => {
    try {
        return localStorage.getItem(RECURRING_LIMIT_DISMISSED_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Permanently dismisses the recurring limit warning so it never shows again.
 */
export const dismissRecurringLimitWarning = () => {
    try {
        localStorage.setItem(RECURRING_LIMIT_DISMISSED_KEY, 'true');
    } catch {
        // localStorage may not be available in some contexts
    }
};

/**
 * Determines if the recurring limit warning modal should be shown.
 * Shows when: count > threshold AND not permanently dismissed.
 *
 * @returns {boolean} True if the warning modal should be displayed
 */
export const shouldShowRecurringLimitWarning = () => {
    if (isRecurringLimitWarningDismissed()) {
        return false;
    }
    return getRecurringSessionCount() > RECURRING_LIMIT_THRESHOLD;
};
